import {
  HiAcademicCap,
  HiDocument,
  HiFolderOpen,
  HiLightningBolt,
  HiPencil,
  HiPhotograph,
  HiSparkles,
} from "react-icons/hi";
import { translate } from "ui/i18n";
import { HomeCarousel } from "ui/pages/app/home/components/HomeCarousel";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import { useNavigate } from "react-router-dom";
import { selectIsPremium, selectUserId } from "domain/auth/auth.selectors";
import { Snackbar } from "ui/pages/snackbar";
import { useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { PrimaryButton } from "ui/pages/app/components/buttons/PrimaryButton";
import { selectPremiumActivitiesCount } from "domain/activities/activities.selectors";
import { selectOnBoardingValues } from "domain/on-boarding/on-boarding.selectors";
import { updateOnBoardingValue } from "ui/utils/on-boarding-storage";
import { i18nKey } from "domain/entities/activities.entities";
import { HomeButton } from "ui/pages/app/home/components/HomeButton";
import { useDisclosure } from "@chakra-ui/hooks";
import { ModalAISearch } from "ui/pages/app/components/activities/AI/ModalAISearch";
import { TechniqueCard } from "ui/pages/app/components/activities/techniques/TechniqueCard";
import { selectRandomTechnique } from "domain/techniques/techniques.selectors";
import { setIsTechniqueLoading } from "domain/techniques/techniques.actions";
import { setIsSeanceLoading } from "domain/seances/seances.actions";
import { selectRandomSeance } from "domain/seances/seances.selectors";
import { selectRandomTutorial } from "domain/tutorials/tutorials.selectors";
import { selectRandomVisualSupport } from "domain/visual-supports/visual-supports.selectors";
import { setIsTutorialLoading } from "domain/tutorials/tutorials.actions";
import { setIsVisualSupportLoading } from "domain/visual-supports/visual-supports.actions";
import { SeanceCard } from "ui/pages/app/components/activities/seances/SeanceCard";
import { TutorialCard } from "../components/activities/tutorials/TutorialCard";
import { VisualSupportCard } from "ui/pages/app/components/activities/visual-supports/VisualSupportCard";
import { PageHeader } from "ui/pages/app/components/PageHeader";
import { useWindowWidth } from "ui/utils/hooks";

export function Home() {
  const isDesktop = useWindowWidth();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const onBoardingValues = useAppSelector(selectOnBoardingValues);
  const isPremium = useAppSelector(selectIsPremium);
  const userId = useAppSelector(selectUserId);
  const premiumActivitiesCount = useAppSelector(selectPremiumActivitiesCount);
  const modalAISearch = useDisclosure();
  const discoverTechnique = useAppSelector(selectRandomTechnique);
  const discoverSeance = useAppSelector(selectRandomSeance);
  const discoverTutorial = useAppSelector(selectRandomTutorial);
  const discoverVisualSupport = useAppSelector(selectRandomVisualSupport);
  const [activityType] = useState<"seance" | "technique">(
    Math.random() > 0.5 ? "seance" : "technique"
  );

  const closeWelcomeMessage = () => {
    if (!onBoardingValues.closedWelcomeMessage) {
      Snackbar.removeSuggestion("welcome-message");
      updateOnBoardingValue(userId, "closedWelcomeMessage", dispatch);
    }
  };

  useEffect(() => {
    if (!onBoardingValues.closedWelcomeMessage) {
      Snackbar.showSuggestion({
        duration: Infinity,
        onClose: closeWelcomeMessage,
        title: translate("ns1:OnBoarding.Snackbar.WelcomeMessageTitle"),
        text: translate("ns1:OnBoarding.Snackbar.WelcomeMessageText"),
        id: "welcome-message",
        type: "success",
      });
    }

    window.addEventListener("beforeunload", closeWelcomeMessage);
    return () => {
      closeWelcomeMessage();
    };
  }, []); //eslint-disable-line

  const handleCreateSeanceClick = () => {
    if (!isPremium) {
      Snackbar.showSubscriptionAlert(
        () => navigate("/account"),
        "create-seance"
      );
    } else navigate("/create-seance");
  };

  const goToAccount = () => {
    navigate("/account");
  };

  const handleTechniqueCardClick = async () => {
    if (!isPremium && !!discoverTechnique?.premium) {
      await Snackbar.removeSubscriptionAlert();
      Snackbar.showSubscriptionAlert(() => navigate("/account"), "technique");
    } else {
      await dispatch(setIsTechniqueLoading(true));
      navigate(`/techniques?id=${discoverTechnique?.id}`);
    }
  };

  const handleSeanceCardClick = async () => {
    if (!isPremium && !!discoverSeance?.premium) {
      await Snackbar.removeSubscriptionAlert();
      Snackbar.showSubscriptionAlert(() => navigate("/account"), "seance");
    } else {
      await dispatch(setIsSeanceLoading(true));
      navigate(`/seances?id=${discoverSeance?.id}`);
    }
  };

  const handleTutorialCardClick = async () => {
    if (!isPremium && !!discoverTutorial?.premium) {
      await Snackbar.removeSubscriptionAlert();
      Snackbar.showSubscriptionAlert(() => navigate("/account"), "tutorial");
    } else {
      await dispatch(setIsTutorialLoading(true));
      navigate(`/tutorials?id=${discoverTutorial?.id}`);
    }
  };

  const handleVisualSupportCardClick = async () => {
    if (!isPremium && !!discoverVisualSupport?.premium) {
      await Snackbar.removeSubscriptionAlert();
      Snackbar.showSubscriptionAlert(
        () => navigate("/account"),
        "visual-support"
      );
    } else {
      await dispatch(setIsVisualSupportLoading(true));
      navigate(`/visual-supports?id=${discoverVisualSupport?.id}`);
    }
  };

  const handleDiscoverClick = (
    type: "Technique" | "Seance" | "Tutorial" | "VisualSupport"
  ) => {
    switch (type) {
      case "Technique":
        return handleTechniqueCardClick();
      case "Seance":
        return handleSeanceCardClick();
      case "Tutorial":
        return handleTutorialCardClick();
      case "VisualSupport":
        return handleVisualSupportCardClick();
    }
  };

  return (
    <div className="w-full px-2 pt-2 md:px-12 md:pt-8">
      {!isDesktop && (
        <PageHeader title={translate("ns1:Routes.Home")} className="mb-4" />
      )}
      <HomeCarousel />
      <div className="mb-10 mt-6 grid w-full grid-cols-3 gap-y-6 md:grid-cols-2 md:gap-x-6 xl:grid-cols-4">
        {!isDesktop && (
          <>
            <HomeButton
              onClick={() => navigate("/techniques")}
              Icon={HiDocument}
              text={translate("ns1:Home.AllTechniques")}
            />
            <HomeButton
              onClick={() => navigate("/seances")}
              Icon={HiFolderOpen}
              text={translate("ns1:Home.AllSeances")}
            />
            <HomeButton
              onClick={() => navigate("/visual-supports")}
              Icon={HiPhotograph}
              text={translate("ns1:Home.VisualSupports")}
            />
            <HomeButton
              onClick={() => navigate("/tutorials")}
              Icon={HiAcademicCap}
              text={translate("ns1:Home.Tutorials")}
            />
          </>
        )}
        <HomeButton
          onClick={handleCreateSeanceClick}
          Icon={HiPencil}
          text={translate("ns1:Home.CreateASeance")}
          disabled={!isPremium}
        />
        <HomeButton
          onClick={modalAISearch.onOpen}
          Icon={HiSparkles}
          text={translate("ns1:Home.FindWithAI")}
        />
      </div>

      <h2 className="mb-6 text-lg md:text-2xl">
        {translate("ns1:Home.ToDiscover")}
      </h2>

      <div className="flex flex-col items-center justify-between rounded-[20px] bg-white p-4 md:flex-row md:px-8 md:py-5">
        <div className="flex items-center">
          <div className="h-6 w-6">
            <HiLightningBolt size={24} className="text-lightGreen-500" />
          </div>
          <span className="ms-2 text-base font-extrabold text-secondaryGrey-900 md:text-lg">
            <Trans
              i18nKey={"Home.BoostMeetings" as i18nKey}
              components={[<span className="text-lightGreen-700" />]}
              values={{ ressources: premiumActivitiesCount }}
            />
          </span>
        </div>
        {!isPremium && (
          <PrimaryButton
            height="36px"
            className="h-[36px]"
            onClick={goToAccount}
          >
            {translate("ns1:Snackbar.LearnMore")}
          </PrimaryButton>
        )}
      </div>

      {/* Random Technique or Seance */}
      <div className="my-6 grid w-full grid-cols-1 gap-x-8 gap-y-6 lg:grid-cols-3">
        {activityType === "technique" && discoverTechnique && (
          <DiscoverSection
            type="Technique"
            title={discoverTechnique.title}
            itemsList={discoverTechnique.activities.map((a) => a.label)}
            handleDiscoverClick={handleDiscoverClick}
          >
            <TechniqueCard
              technique={discoverTechnique}
              disableFlip
              onClick={handleTechniqueCardClick}
              compact
            />
          </DiscoverSection>
        )}
        {activityType === "seance" && discoverSeance && (
          <DiscoverSection
            type="Seance"
            title={discoverSeance.title}
            handleDiscoverClick={handleDiscoverClick}
            text={discoverSeance.objectif}
          >
            <SeanceCard
              seance={discoverSeance}
              onClick={handleSeanceCardClick}
              compact
            />
          </DiscoverSection>
        )}
        {/* Tutorial */}
        {discoverTutorial && (
          <DiscoverSection
            type="Tutorial"
            title={discoverTutorial.title}
            text={discoverTutorial.content}
            handleDiscoverClick={handleDiscoverClick}
          >
            <TutorialCard
              tutorial={discoverTutorial}
              onClick={handleTutorialCardClick}
              compact
            />
          </DiscoverSection>
        )}
        {/* Visual Support */}
        {discoverVisualSupport && (
          <DiscoverSection
            type="VisualSupport"
            title={discoverVisualSupport.title}
            itemsList={discoverVisualSupport.techniquesTitles || []}
            handleDiscoverClick={handleDiscoverClick}
          >
            <VisualSupportCard
              support={discoverVisualSupport}
              onClick={handleVisualSupportCardClick}
              compact
            />
          </DiscoverSection>
        )}
      </div>

      <ModalAISearch
        isOpen={modalAISearch.isOpen}
        onClose={modalAISearch.onClose}
        requestFeedback
        redirect
      />
    </div>
  );
}

const DiscoverSection = ({
  type,
  title,
  children,
  itemsList,
  text,
  handleDiscoverClick,
}: {
  type: "Technique" | "Seance" | "Tutorial" | "VisualSupport";
  title: string;
  children: React.ReactNode;
  itemsList?: string[];
  text?: string;
  handleDiscoverClick: (
    type: "Technique" | "Seance" | "Tutorial" | "VisualSupport"
  ) => Promise<void>;
}) => {
  return (
    <div className="flex flex-col justify-between rounded-3xl bg-secondaryGrey-400 px-3 py-4 md:p-8 md:pb-8 md:pt-10">
      <div>
        <div className="mx-auto max-w-[220px]">{children}</div>
        <p className="mt-10 text-base font-bold text-darkGrey-600 md:mt-6 md:text-lg">
          {translate(`ns1:Home.${type}`)}
        </p>
        <p className="mt-1 text-xl font-bold text-secondaryGrey-900">{title}</p>
        <div className="mt-4 text-sm font-medium text-secondaryGrey-900">
          {type === "Technique" && itemsList && (
            <ul>
              {itemsList.map((item, index) => (
                <li key={`tech-${index}`}>{item}</li>
              ))}
            </ul>
          )}
          {type === "Seance" && text && (
            <>
              <p>{translate("ns1:Home.Objective")}</p>
              <p>{text}</p>
            </>
          )}
          {type === "Tutorial" && text && (
            <p className="!line-clamp-2">{text}</p>
          )}
          {type === "VisualSupport" && itemsList && (
            <>
              <p>{translate("ns1:Home.ToUseIn")}</p>
              <ul>
                {itemsList.map((item, index) => (
                  <li key={`visual-${index}`}>{item}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
      <PrimaryButton
        className="mt-6 w-full"
        onClick={() => handleDiscoverClick(type)}
      >
        {translate("ns1:Button.Discover")}
      </PrimaryButton>
    </div>
  );
};
